import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, 
         BarChart, Bar, ScatterChart, Scatter, PieChart, Pie, Cell,
         ComposedChart, Area, AreaChart } from 'recharts';
import { Calendar, TrendingUp, Brain, Zap, AlertTriangle, Target, 
         Download, Filter, Eye, Activity, BarChart3, PieChart as PieIcon } from 'lucide-react';

const AdvancedAnalytics = () => {
  const [analyticsData, setAnalyticsData] = useState({});
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('24h');
  const [selectedMetric, setSelectedMetric] = useState('response_time');
  const [predictions, setPredictions] = useState([]);

  useEffect(() => {
    fetchAdvancedAnalytics();
    const interval = setInterval(fetchAdvancedAnalytics, 30000);
    return () => clearInterval(interval);
  }, [timeRange, selectedMetric]);

  const fetchAdvancedAnalytics = async () => {
    try {
      // Fetch dashboard stats first (we know this works)
      const dashboardRes = await fetch(`/api/dashboard/stats`);
      const dashboardStats = dashboardRes.ok ? await dashboardRes.json() : null;

      // Try to fetch new analytics data, fallback to mock data
      let analyticsMetrics = generateMockAnalytics();
      let trends = generateMockTrends();
      let predictionData = generateMockPredictions();
      let correlations = generateMockCorrelations();

      try {
        const metricsRes = await fetch(`/api/analytics/metrics?range=${timeRange}`);
        if (metricsRes.ok) {
          analyticsMetrics = await metricsRes.json();
        }
      } catch (e) {
        console.warn('Analytics metrics fetch failed, using mock data');
      }

      try {
        const trendsRes = await fetch(`/api/analytics/trends?range=${timeRange}`);
        if (trendsRes.ok) {
          trends = await trendsRes.json();
        }
      } catch (e) {
        console.warn('Analytics trends fetch failed, using mock data');
      }

      setAnalyticsData({
        metrics: analyticsMetrics,
        trends: trends,
        correlations: correlations,
        anomalyPatterns: generateAnomalyPatterns(),
        performanceMetrics: generatePerformanceMetrics(),
        predictiveInsights: generatePredictiveInsights(),
        dashboardStats: dashboardStats
      });
      setPredictions(predictionData);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      // Use all mock data on error
      setAnalyticsData({
        metrics: generateMockAnalytics(),
        trends: generateMockTrends(),
        correlations: generateMockCorrelations(),
        anomalyPatterns: generateAnomalyPatterns(),
        performanceMetrics: generatePerformanceMetrics(),
        predictiveInsights: generatePredictiveInsights()
      });
      setPredictions(generateMockPredictions());
      setLoading(false);
    }
  };

  const generateMockAnalytics = () => {
    const hours = Array.from({ length: 24 }, (_, i) => {
      const now = new Date();
      now.setHours(now.getHours() - (23 - i));
      return now.toISOString();
    });

    return {
      anomalyRate: hours.map((time, i) => ({
        timestamp: time,
        rate: Math.random() * 0.15 + 0.02,
        count: Math.floor(Math.random() * 8),
        severity: Math.random() * 0.8 + 0.2
      })),
      detectionAccuracy: {
        precision: 0.94,
        recall: 0.89,
        f1Score: 0.915,
        falsePositiveRate: 0.06
      },
      serviceHealth: [
        { service: 'api-gateway', health: 0.98, responseTime: 125, errorRate: 0.002 },
        { service: 'user-service', health: 0.95, responseTime: 89, errorRate: 0.001 },
        { service: 'order-service', health: 0.92, responseTime: 156, errorRate: 0.003 },
        { service: 'payment-service', health: 0.97, responseTime: 203, errorRate: 0.001 },
        { service: 'cache-service', health: 0.99, responseTime: 12, errorRate: 0.000 }
      ]
    };
  };

  const generateMockTrends = () => ({
    weekly: [
      { day: 'Monday', anomalies: 12, avgResponseTime: 145, uptime: 99.8 },
      { day: 'Tuesday', anomalies: 8, avgResponseTime: 132, uptime: 99.9 },
      { day: 'Wednesday', anomalies: 15, avgResponseTime: 167, uptime: 99.7 },
      { day: 'Thursday', anomalies: 6, avgResponseTime: 128, uptime: 99.95 },
      { day: 'Friday', anomalies: 18, avgResponseTime: 189, uptime: 99.6 },
      { day: 'Saturday', anomalies: 4, avgResponseTime: 98, uptime: 99.99 },
      { day: 'Sunday', anomalies: 7, avgResponseTime: 112, uptime: 99.94 }
    ],
    monthly: [
      { month: 'Jan', anomalies: 234, mttr: 12.5, availability: 99.85 },
      { month: 'Feb', anomalies: 189, mttr: 9.8, availability: 99.92 },
      { month: 'Mar', anomalies: 156, mttr: 8.2, availability: 99.94 }
    ]
  });

  const generateMockPredictions = () => {
    const now = new Date();
    return Array.from({ length: 12 }, (_, i) => {
      const time = new Date(now);
      time.setHours(time.getHours() + i);
      return {
        timestamp: time.toISOString(),
        predicted: Math.random() * 200 + 100,
        confidence: Math.random() * 0.3 + 0.7,
        anomalyRisk: Math.random() * 0.4,
        actual: i < 6 ? Math.random() * 200 + 100 : null
      };
    });
  };

  const generateMockCorrelations = () => ({
    matrix: [
      ['response_time', 'cpu_usage', 0.78],
      ['response_time', 'memory_usage', 0.65],
      ['response_time', 'error_rate', 0.82],
      ['cpu_usage', 'memory_usage', 0.71],
      ['error_rate', 'request_count', -0.23]
    ]
  });

  const generateAnomalyPatterns = () => ({
    timePatterns: [
      { hour: 9, frequency: 0.15, type: 'peak_traffic' },
      { hour: 12, frequency: 0.22, type: 'lunch_spike' },
      { hour: 17, frequency: 0.28, type: 'evening_peak' },
      { hour: 2, frequency: 0.08, type: 'maintenance' }
    ],
    typeDistribution: [
      { name: 'Response Time', value: 45, color: '#8884d8' },
      { name: 'Error Rate', value: 25, color: '#82ca9d' },
      { name: 'Resource Usage', value: 20, color: '#ffc658' },
      { name: 'Network Issues', value: 10, color: '#ff7c7c' }
    ]
  });

  const generatePerformanceMetrics = () => ({
    detectionLatency: 85, // ms
    modelAccuracy: 94.2, // %
    throughput: 1250, // predictions/sec
    resourceUsage: 68 // % CPU
  });

  const generatePredictiveInsights = () => [
    {
      insight: "API Gateway likely to experience high latency in next 2 hours",
      confidence: 0.87,
      impact: "high",
      recommendation: "Consider scaling up instances preemptively"
    },
    {
      insight: "User service shows pattern indicating potential memory leak",
      confidence: 0.74,
      impact: "medium", 
      recommendation: "Schedule maintenance window for investigation"
    },
    {
      insight: "Payment service correlates with increased error rates during peak hours",
      confidence: 0.91,
      impact: "high",
      recommendation: "Implement circuit breaker pattern"
    }
  ];

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Advanced Analytics</h1>
          <p className="text-gray-600">Deep insights into system performance and predictions</p>
        </div>
        <div className="flex gap-4">
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            className="px-4 py-2 border rounded-lg"
          >
            <option value="1h">Last Hour</option>
            <option value="24h">Last 24 Hours</option>
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
          </select>
          <button className="px-4 py-2 bg-blue-600 text-white rounded-lg flex items-center gap-2">
            <Download size={16} />
            Export Report
          </button>
        </div>
      </div>

      {/* Key Metrics Row */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Brain className="h-4 w-4 text-purple-600" />
              Model Accuracy
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{analyticsData.performanceMetrics?.modelAccuracy}%</div>
            <p className="text-xs text-green-600">+2.3% vs last week</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Zap className="h-4 w-4 text-yellow-600" />
              Detection Latency
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{analyticsData.performanceMetrics?.detectionLatency}ms</div>
            <p className="text-xs text-green-600">-15ms vs last week</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Activity className="h-4 w-4 text-blue-600" />
              Throughput
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{analyticsData.performanceMetrics?.throughput}</div>
            <p className="text-xs text-gray-500">predictions/sec</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Target className="h-4 w-4 text-green-600" />
              F1-Score
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{analyticsData.metrics?.detectionAccuracy?.f1Score}</div>
            <p className="text-xs text-green-600">+0.02 vs last week</p>
          </CardContent>
        </Card>
      </div>

      {/* Anomaly Rate Trends */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-blue-600" />
            Anomaly Detection Trends
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <ComposedChart data={analyticsData.metrics?.anomalyRate || []}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="timestamp" 
                tickFormatter={(time) => new Date(time).toLocaleTimeString()}
              />
              <YAxis yAxisId="left" />
              <YAxis yAxisId="right" orientation="right" />
              <Tooltip 
                labelFormatter={(time) => new Date(time).toLocaleString()}
              />
              <Bar yAxisId="left" dataKey="count" fill="#8884d8" name="Anomaly Count" />
              <Line 
                yAxisId="right" 
                type="monotone" 
                dataKey="rate" 
                stroke="#82ca9d" 
                strokeWidth={2}
                name="Detection Rate"
              />
            </ComposedChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Predictions and Service Health */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5 text-purple-600" />
              Predictive Forecasting
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={predictions}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="timestamp" 
                  tickFormatter={(time) => new Date(time).toLocaleTimeString()}
                />
                <YAxis />
                <Tooltip 
                  labelFormatter={(time) => new Date(time).toLocaleString()}
                />
                <Area 
                  type="monotone" 
                  dataKey="predicted" 
                  stroke="#8884d8" 
                  fill="#8884d8" 
                  fillOpacity={0.3}
                  name="Predicted Value"
                />
                <Line 
                  type="monotone" 
                  dataKey="actual" 
                  stroke="#ff7c7c" 
                  strokeWidth={2}
                  name="Actual Value"
                />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-green-600" />
              Service Health Matrix
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {analyticsData.metrics?.serviceHealth?.map((service, index) => (
                <div key={service.service} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <div className="font-medium">{service.service}</div>
                    <div className="text-sm text-gray-600">
                      RT: {service.responseTime}ms | ER: {(service.errorRate * 100).toFixed(3)}%
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div 
                      className={`w-3 h-3 rounded-full ${
                        service.health > 0.95 ? 'bg-green-500' : 
                        service.health > 0.90 ? 'bg-yellow-500' : 'bg-red-500'
                      }`}
                    />
                    <span className="font-bold">{(service.health * 100).toFixed(1)}%</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Anomaly Patterns and Correlations */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <PieIcon className="h-5 w-5 text-orange-600" />
              Anomaly Type Distribution
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={analyticsData.anomalyPatterns?.typeDistribution || []}
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  dataKey="value"
                >
                  {analyticsData.anomalyPatterns?.typeDistribution?.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
            <div className="flex flex-wrap gap-2 mt-4">
              {analyticsData.anomalyPatterns?.typeDistribution?.map((entry, index) => (
                <div key={entry.name} className="flex items-center gap-1">
                  <div 
                    className="w-3 h-3 rounded-full" 
                    style={{ backgroundColor: COLORS[index % COLORS.length] }}
                  />
                  <span className="text-sm">{entry.name}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-600" />
              Predictive Insights
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {analyticsData.predictiveInsights?.map((insight, index) => (
                <div 
                  key={index} 
                  className={`p-4 rounded-lg border-l-4 ${
                    insight.impact === 'high' ? 'border-red-500 bg-red-50' :
                    insight.impact === 'medium' ? 'border-yellow-500 bg-yellow-50' :
                    'border-green-500 bg-green-50'
                  }`}
                >
                  <div className="flex justify-between items-start mb-2">
                    <div className="text-sm font-medium">{insight.insight}</div>
                    <div className="text-xs bg-gray-200 px-2 py-1 rounded">
                      {(insight.confidence * 100).toFixed(0)}%
                    </div>
                  </div>
                  <div className="text-xs text-gray-600">{insight.recommendation}</div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Weekly Trends */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-indigo-600" />
            Weekly Performance Trends
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={analyticsData.trends?.weekly || []}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="day" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="anomalies" fill="#8884d8" name="Anomalies Detected" />
              <Bar dataKey="avgResponseTime" fill="#82ca9d" name="Avg Response Time (ms)" />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdvancedAnalytics;